import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DialogTranscription } from '../../../models/CallAttempt/CallAttempt';

type UseTranscriptionSearchParams = {
  transcription: DialogTranscription[];
};

type TranscriptionSearchMatch = {
  lineIndex: number;
  startOffset: number;
  length: number;
};

type IndexedTranscriptionSearchMatch = TranscriptionSearchMatch & {
  globalIndex: number;
};

export const useTranscriptionSearch = (
  params: UseTranscriptionSearchParams | DialogTranscription[],
) => {
  const transcription = Array.isArray(params) ? params : params.transcription;
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);

  const matches = useMemo<TranscriptionSearchMatch[]>(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    if (!normalizedQuery) {
      return [];
    }

    return transcription.reduce<TranscriptionSearchMatch[]>(
      (acc, entry, lineIndex) => {
        const text = entry.value ?? '';
        const normalizedText = text.toLowerCase();

        let searchFromIndex = 0;

        while (searchFromIndex < normalizedText.length) {
          const matchIndex = normalizedText.indexOf(
            normalizedQuery,
            searchFromIndex,
          );

          if (matchIndex === -1) {
            break;
          }

          acc.push({
            lineIndex,
            startOffset: matchIndex,
            length: normalizedQuery.length,
          });

          searchFromIndex = matchIndex + normalizedQuery.length;
        }

        return acc;
      },
      [],
    );
  }, [searchQuery, transcription]);

  const totalMatches = matches.length;

  const matchesByLine = useMemo<
    Record<number, IndexedTranscriptionSearchMatch[]>
  >(() => {
    return matches.reduce<Record<number, IndexedTranscriptionSearchMatch[]>>(
      (acc, match, globalIndex) => {
        const indexedMatch: IndexedTranscriptionSearchMatch = {
          ...match,
          globalIndex,
        };

        if (!acc[match.lineIndex]) {
          acc[match.lineIndex] = [indexedMatch];
          return acc;
        }

        acc[match.lineIndex].push(indexedMatch);

        return acc;
      },
      {},
    );
  }, [matches]);

  useEffect(() => {
    setCurrentMatchIndex(0);
  }, [searchQuery]);

  useEffect(() => {
    if (totalMatches === 0) {
      setCurrentMatchIndex(0);
      return;
    }

    if (currentMatchIndex >= totalMatches) {
      setCurrentMatchIndex(0);
    }
  }, [currentMatchIndex, totalMatches]);

  const goToNextMatch = useCallback(() => {
    if (totalMatches === 0) {
      return;
    }

    setCurrentMatchIndex((prevIndex) => (prevIndex + 1) % totalMatches);
  }, [totalMatches]);

  const goToPrevMatch = useCallback(() => {
    if (totalMatches === 0) {
      return;
    }

    setCurrentMatchIndex(
      (prevIndex) => (prevIndex - 1 + totalMatches) % totalMatches,
    );
  }, [totalMatches]);

  const highlightText = useCallback(
    (text: string, lineIndex: number): React.ReactNode => {
      if (!searchQuery.trim()) {
        return text;
      }

      const lineMatches = matchesByLine[lineIndex] ?? [];

      if (lineMatches.length === 0) {
        return text;
      }

      const fragments: React.ReactNode[] = [];
      let cursor = 0;

      lineMatches.forEach((match) => {
        const startOffset = Math.max(match.startOffset, cursor);
        const endOffset = Math.min(
          match.startOffset + match.length,
          text.length,
        );

        if (startOffset >= endOffset) {
          return;
        }

        if (cursor < startOffset) {
          fragments.push(text.slice(cursor, startOffset));
        }

        fragments.push(
          React.createElement(
            'mark',
            {
              key: `${lineIndex}-${match.startOffset}-${match.length}`,
              className:
                match.globalIndex === currentMatchIndex
                  ? 'transcription-match-active'
                  : 'transcription-match',
            },
            text.slice(startOffset, endOffset),
          ),
        );

        cursor = endOffset;
      });

      if (cursor < text.length) {
        fragments.push(text.slice(cursor));
      }

      return fragments;
    },
    [currentMatchIndex, matchesByLine, searchQuery],
  );

  return {
    searchQuery,
    setSearchQuery,
    matches,
    currentMatchIndex,
    totalMatches,
    goToNextMatch,
    goToPrevMatch,
    highlightText,
  };
};
